import { useEffect, useRef, useState } from 'react';
import BusinessIcon from '@mui/icons-material/Business';
import { Box, Link, Popover, TextField, Typography } from '@mui/material';
import { ConditionalLoader, Section } from 'dashboard409a/pages/Operations/components';
import Avatar from 'dashboard409a/pages/Operations/components/Avatar/Avatar';
import { CategoryBadge, ContactSection, Container, HeaderRow, InfoRow, StyledLinkSpan } from './ValuedEntity.styles';
import { ValuedEntityProps } from './ValuedEntity.types';

const ValuedEntity = ({ valuedEntity, isLoading, companyFolder, onUpdateCompanyFolder }: ValuedEntityProps) => {
  const [isEditingCompanyFolder, setIsEditingCompanyFolder] = useState(false);
  const [companyFolderValue, setCompanyFolderValue] = useState(companyFolder || '');
  const [isSavingCompanyFolder, setIsSavingCompanyFolder] = useState(false);
  const [noticeAnchorEl, setNoticeAnchorEl] = useState<HTMLElement | null>(null);
  const noticeTextRef = useRef<HTMLSpanElement>(null);
  const [isNoticeTruncated, setIsNoticeTruncated] = useState(false);
  const [contactNoticeAnchorEl, setContactNoticeAnchorEl] = useState<HTMLElement | null>(null);
  const contactNoticeRef = useRef<HTMLSpanElement>(null);
  const [isContactNoticeTruncated, setIsContactNoticeTruncated] = useState(false);

  useEffect(() => {
    const el = noticeTextRef.current;
    if (el) {
      setIsNoticeTruncated(el.scrollHeight > el.clientHeight);
    }
  }, [valuedEntity?.valuedEntityNotice]);

  useEffect(() => {
    const el = contactNoticeRef.current;
    if (el) {
      setIsContactNoticeTruncated(el.scrollHeight > el.clientHeight);
    }
  }, [valuedEntity?.primaryContactNotice]);

  // Update local state when companyFolder prop changes
  useEffect(() => {
    if (companyFolder !== undefined) {
      setCompanyFolderValue(companyFolder || '');
    }
  }, [companyFolder]);

  const handleCompanyFolderBlur = async () => {
    if (!onUpdateCompanyFolder) {
      return;
    }
    
    const folderName = companyFolderValue.trim();
    // Extract just the folder name (remove /Valuations/ prefix if present)
    const folderNameOnly = folderName.replace(/^\/?Valuations\/?/, '').trim();
    
    // If the value hasn't changed, just exit edit mode
    if (folderNameOnly === (companyFolder || '')) {
      setIsEditingCompanyFolder(false);
      return;
    }

    setIsSavingCompanyFolder(true);
    try {
      await onUpdateCompanyFolder(folderNameOnly);
      setIsEditingCompanyFolder(false);
    } catch (error) {
      // Error is handled by the parent component
    } finally {
      setIsSavingCompanyFolder(false);
    }
  };

  const handleCompanyFolderKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleCompanyFolderBlur();
    } else if (e.key === 'Escape') {
      setCompanyFolderValue(companyFolder || '');
      setIsEditingCompanyFolder(false);
    }
  };

  // Display full path: /Valuations/{folderName}
  const displayCompanyFolderPath = companyFolder 
    ? `/Valuations/${companyFolder}` 
    : '';

  return (
    <Section title="Valued Entity">
      <Container>
        <HeaderRow id="company-name">
          <BusinessIcon color="primary" fontSize="small" />
          <ConditionalLoader isLoading={isLoading} width={150}>
            {valuedEntity?.companyUrl ? (
              <Link
                href={valuedEntity.companyUrl}
                target="_blank"
                rel="noopener noreferrer"
              >
                <StyledLinkSpan>{valuedEntity.companyName || '-'}</StyledLinkSpan>
              </Link>
            ) : (
              <StyledLinkSpan>{valuedEntity?.companyName || '-'}</StyledLinkSpan>
            )}
          </ConditionalLoader>
        </HeaderRow>
        {valuedEntity?.valuedEntityNotice && (
          <Box>
            <Typography
              ref={noticeTextRef}
              variant="body2"
              sx={{
                display: '-webkit-box',
                WebkitLineClamp: 1,
                WebkitBoxOrient: 'vertical',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                wordBreak: 'break-word',
              }}
            >
              NOTICE: {valuedEntity.valuedEntityNotice}
            </Typography>
            {isNoticeTruncated && (
              <>
                <Typography
                  component="span"
                  variant="body2"
                  color="primary"
                  sx={{ cursor: 'pointer', fontSize: '0.75rem' }}
                  onClick={e => setNoticeAnchorEl(e.currentTarget)}
                >
                  show more
                </Typography>
                <Popover
                  open={Boolean(noticeAnchorEl)}
                  anchorEl={noticeAnchorEl}
                  onClose={() => setNoticeAnchorEl(null)}
                  anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
                  transformOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                >
                  <Box sx={{ p: 2, maxWidth: 400 }}>
                    <Typography variant="body2">NOTICE: {valuedEntity.valuedEntityNotice}</Typography>
                  </Box>
                </Popover>
              </>
            )}
          </Box>
        )}
        <InfoRow>
          <ContactSection>
            <Typography id="primary-contact" variant="body2" color="text.secondary">
              Primary Contact:
            </Typography>
            <ConditionalLoader isLoading={isLoading} width={150}>
              <Typography fontWeight="bold">{valuedEntity?.primaryContact || '-'}</Typography>
            </ConditionalLoader>
            {valuedEntity?.primaryContactNotice && (
              <Box>
                <Typography
                  ref={contactNoticeRef}
                  variant="body2"
                  sx={{
                    display: '-webkit-box',
                    WebkitLineClamp: 1,
                    WebkitBoxOrient: 'vertical',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    wordBreak: 'break-word',
                  }}
                >
                  NOTICE: {valuedEntity.primaryContactNotice}
                </Typography>
                {isContactNoticeTruncated && (
                  <>
                    <Typography
                      component="span"
                      variant="body2"
                      color="primary"
                      sx={{ cursor: 'pointer', fontSize: '0.75rem' }}
                      onClick={e => setContactNoticeAnchorEl(e.currentTarget)}
                    >
                      show more
                    </Typography>
                    <Popover
                      open={Boolean(contactNoticeAnchorEl)}
                      anchorEl={contactNoticeAnchorEl}
                      onClose={() => setContactNoticeAnchorEl(null)}
                      anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
                      transformOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                    >
                      <Box sx={{ p: 2, maxWidth: 400 }}>
                        <Typography variant="body2">NOTICE: {valuedEntity.primaryContactNotice}</Typography>
                      </Box>
                    </Popover>
                  </>
                )}
              </Box>
            )}
            <Typography id="category" variant="body2" color="text.secondary" mt={1}>
              Category:
            </Typography>
            <ConditionalLoader isLoading={isLoading} width={150}>
              <CategoryBadge>{valuedEntity?.category || '-'}</CategoryBadge>
            </ConditionalLoader>
            <Typography id="dropbox-folder" variant="body2" color="text.secondary" mt={1}>
            Dropbox: Company Folder
            </Typography>
            <ConditionalLoader isLoading={isLoading} width={200}>
              {isEditingCompanyFolder ? (
                <TextField
                  value={companyFolderValue ? `/Valuations/${companyFolderValue}` : '/Valuations/'}
                  onChange={e => {
                    const {value} = e.target;
                    // Remove /Valuations/ prefix for editing
                    const folderName = value.replace(/^\/?Valuations\/?/, '').trim();
                    setCompanyFolderValue(folderName);
                  }}
                  onBlur={handleCompanyFolderBlur}
                  onKeyDown={handleCompanyFolderKeyDown}
                  disabled={isSavingCompanyFolder}
                  size="small"
                  fullWidth
                  autoFocus
                  sx={{ mt: 0.5 }}
                />
              ) : (
                <Typography
                  onClick={() => onUpdateCompanyFolder && setIsEditingCompanyFolder(true)}
                  sx={{
                    mt: 0.5,
                    cursor: onUpdateCompanyFolder ? 'pointer' : 'default',
                    color: onUpdateCompanyFolder ? 'primary.main' : 'text.primary',
                    '&:hover': onUpdateCompanyFolder ? { textDecoration: 'underline' } : {},
                  }}
                >
                  {displayCompanyFolderPath || '-'}
                </Typography>
              )}
            </ConditionalLoader>
          </ContactSection>
          {valuedEntity?.salesRep && (
            <Box>
              <Typography variant="body2" color="text.secondary" mb={0.5}>
              Sales Rep:
              </Typography>
              <Avatar
                src={valuedEntity.salesRepImageSrc || ''}
                name={valuedEntity.salesRep}
                roleTitle="Sales Rep"
                isLoading={isLoading}
              />
            </Box>
          )}  
        </InfoRow>
      </Container>
    </Section>
  );
};

export default ValuedEntity;
